import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Link,
  Checkbox,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import axios from '../utils/axios';
import { useNotify } from '../hooks';

// Фоллбэк — группы прав. Единый источник правды — GET /users/permission-groups.
// Держим пустым, чтобы не было рассинхрона с бэкендом; до прихода API показываем лоадер.
const FALLBACK_PERMISSION_GROUPS = [];

// Маркетплейсы для скоупа доступа к данным (id ↔ название)
const MARKETPLACES = [
  { id: 1, label: 'Wildberries' },
  { id: 2, label: 'Ozon' },
  { id: 3, label: 'Yandex' },
];

// Плоский справочник имён (строится из групп; безопасен при пустом массиве)
const buildFlatNames = (groups) => {
  const flat = {};
  for (const g of groups || []) Object.assign(flat, g.permissions || {});
  return flat;
};

/** Убирает устаревшие ключи (packing/picking/shipmentsMs и т.п.), которых нет в реестре. */
const sanitizeClientPermissions = (perms, knownNames) => {
  if (!perms || typeof perms !== 'object') return {};
  const known = knownNames && typeof knownNames === 'object' ? knownNames : {};
  if (!Object.keys(known).length) return { ...perms };
  const cleaned = {};
  for (const [key, value] of Object.entries(perms)) {
    if (Object.prototype.hasOwnProperty.call(known, key)) cleaned[key] = value;
  }
  return cleaned;
};

// Разбор data_scope (может прийти как null / объект / строка JSON — как и permissions)
const parseDataScope = (raw) => {
  let ds = raw;
  if (typeof ds === 'string') {
    try {
      ds = JSON.parse(ds);
      if (typeof ds === 'string') ds = JSON.parse(ds);
    } catch (e) {
      ds = null;
    }
  }
  if (!ds || typeof ds !== 'object') return null;
  return ds;
};

// Диалог настройки прав
function PermissionsDialog({ open, onClose, user, onSave, permissionGroups }) {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  // Доступ к данным (data_scope)
  const [fullAccess, setFullAccess] = useState(true);
  const [scopeOrgs, setScopeOrgs] = useState([]);
  const [scopeMps, setScopeMps] = useState([]);
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);

  const groupsLoading = !permissionGroups || permissionGroups.length === 0;
  const station = Boolean(user?.is_station);

  useEffect(() => {
    if (user) {
      let perms = user.permissions || {};
      if (typeof perms === 'string') {
        try {
          perms = JSON.parse(perms);
          if (typeof perms === 'string') perms = JSON.parse(perms);
        } catch (e) {
          perms = {};
        }
      }
      setPermissions(sanitizeClientPermissions(perms, buildFlatNames(permissionGroups)));

      const ds = parseDataScope(user.data_scope);
      if (ds === null) {
        setFullAccess(true);
        setScopeOrgs([]);
        setScopeMps([]);
      } else {
        setFullAccess(false);
        setScopeOrgs(Array.isArray(ds.organizations) ? ds.organizations : []);
        setScopeMps(Array.isArray(ds.marketplaces) ? ds.marketplaces.map(Number).filter(n => !Number.isNaN(n)) : []);
      }
    }
  }, [user, permissionGroups]);

  // Загрузка доступных организаций (для admin — все, иначе урезано бэкендом по скоупу)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setOrgsLoading(true);
        const resp = await axios.get('/filters/organizations');
        if (cancelled) return;
        setOrgOptions(resp.data?.organizations || []);
      } catch (e) {
        if (!cancelled) setOrgOptions([]);
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleTogglePermission = (permission) => {
    setPermissions(prev => ({ ...prev, [permission]: !prev[permission] }));
  };

  const handleGroupToggle = (keys, value) => {
    setPermissions(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = value; });
      return next;
    });
  };

  const handleToggleMp = (id) => {
    setScopeMps(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // null = полный доступ; иначе две независимые оси (пустая ось = «все по ней»)
      const dataScope = fullAccess
        ? null
        : { organizations: scopeOrgs, marketplaces: scopeMps };
      const cleaned = sanitizeClientPermissions(permissions, buildFlatNames(permissionGroups));
      await onSave(cleaned, dataScope);
      onClose();
    } catch (error) {
      // Ошибку (400/403) показывает родитель через showError; диалог остаётся открытым
      console.error('Ошибка при сохранении прав:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Настройка прав доступа</Typography>
        <Typography variant="body2" color="text.secondary">
          {user ? `${user.firstName} ${user.lastName}` : ''}
        </Typography>
        {station && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            У станции всегда открыт цех. Для печати этикеток можно отдельно включить «Сетевая печать — очередь».
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {groupsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Загрузка списка прав…</Typography>
            </Box>
          ) : permissionGroups.map((grp, idx) => {
            const keys = Object.keys(grp.permissions);
            const allOn = keys.every(k => !!permissions[k]);
            const noneOn = keys.every(k => !permissions[k]);
            return (
              <Box key={grp.group} sx={{ mb: idx < permissionGroups.length - 1 ? 1 : 0 }}>
                {/* Заголовок группы */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">{grp.group}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Link
                      component="button"
                      variant="caption"
                      underline="hover"
                      disabled={loading || station || allOn}
                      onClick={() => handleGroupToggle(keys, true)}
                      sx={{ cursor: 'pointer', color: (station || allOn) ? 'text.disabled' : 'primary.main' }}
                    >
                      все
                    </Link>
                    <Link
                      component="button"
                      variant="caption"
                      underline="hover"
                      disabled={loading || station || noneOn}
                      onClick={() => handleGroupToggle(keys, false)}
                      sx={{ cursor: 'pointer', color: (station || noneOn) ? 'text.disabled' : 'primary.main' }}
                    >
                      снять
                    </Link>
                  </Box>
                </Box>
                {/* Список прав */}
                <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                  {Object.entries(grp.permissions).map(([key, label]) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          size="small"
                          checked={station && key === 'production' ? true : !!permissions[key]}
                          onChange={() => handleTogglePermission(key)}
                          disabled={loading || (station && key !== 'printQueue')}
                        />
                      }
                      label={<Typography variant="body2">{label}</Typography>}
                      sx={{ my: -0.3 }}
                    />
                  ))}
                </Box>
                {idx < permissionGroups.length - 1 && <Divider sx={{ mt: 1 }} />}
              </Box>
            );
          })}
        </Box>

        {/* Доступ к данным (data_scope) */}
        <Divider sx={{ my: 2 }} />
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">Доступ к данным</Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={fullAccess}
                  onChange={(e) => setFullAccess(e.target.checked)}
                  disabled={loading}
                />
              }
              label={<Typography variant="body2">Полный доступ</Typography>}
              sx={{ mr: 0 }}
            />
          </Box>
          {fullAccess ? (
            <Typography variant="caption" color="text.secondary">
              Пользователь видит данные всех организаций и маркетплейсов.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth size="small" disabled={loading}>
                <InputLabel id="scope-orgs-label">Организации</InputLabel>
                <Select
                  labelId="scope-orgs-label"
                  multiple
                  value={scopeOrgs}
                  onChange={(e) => setScopeOrgs(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  label="Организации"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((v) => <Chip key={v} label={v} size="small" sx={{ height: 20 }} />)}
                    </Box>
                  )}
                >
                  {orgsLoading && (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} /> Загрузка…
                    </MenuItem>
                  )}
                  {!orgsLoading && orgOptions.length === 0 && (
                    <MenuItem disabled>Нет доступных организаций</MenuItem>
                  )}
                  {orgOptions.map((org) => (
                    <MenuItem key={org} value={org}>
                      <Checkbox size="small" checked={scopeOrgs.indexOf(org) > -1} />
                      <Typography variant="body2">{org}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Маркетплейсы</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', ml: 1 }}>
                  {MARKETPLACES.map((mp) => (
                    <FormControlLabel
                      key={mp.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={scopeMps.includes(mp.id)}
                          onChange={() => handleToggleMp(mp.id)}
                          disabled={loading}
                        />
                      }
                      label={<Typography variant="body2">{mp.label}</Typography>}
                      sx={{ my: -0.3 }}
                    />
                  ))}
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Пустой выбор по оси = доступ ко всем значениям этой оси (все организации / все маркетплейсы).
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Отмена</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : null}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Диалог редактирования пользователя
function UserDialog({ open, handleClose, user, onSave, onPermissionsOpen }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'worker',
    ms_id: '',
    employee_number: '',
    is_station: false,
    isActive: true,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        ...user,
        password: '',
        ms_id: user.ms_id || '',
        employee_number: user.employee_number || '',
        is_station: Boolean(user.is_station),
      });
    } else {
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        ms_id: '',
        employee_number: '',
        role: 'worker',
        is_station: false,
        isActive: true,
      });
    }
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email обязателен';
    if (!user && !formData.password) newErrors.password = 'Пароль обязателен';
    if (!formData.firstName) newErrors.firstName = 'Имя обязательно';
    if (!formData.lastName) newErrors.lastName = 'Фамилия обязательна';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{user ? 'Редактирование пользователя' : 'Новый пользователь'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            size="small"
          />
          <TextField
            label="Пароль"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            error={!!errors.password}
            helperText={errors.password}
            fullWidth
            size="small"
            placeholder={user ? 'Оставьте пустым, чтобы не менять' : ''}
          />
          <TextField
            label="Имя"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            error={!!errors.firstName}
            helperText={errors.firstName}
            fullWidth
            size="small"
          />
          <TextField
            label="Фамилия"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            error={!!errors.lastName}
            helperText={errors.lastName}
            fullWidth
            size="small"
          />
          <TextField
            label="ID в МоёмСкладе"
            value={formData.ms_id}
            onChange={(e) => setFormData({ ...formData, ms_id: e.target.value })}
            fullWidth
            size="small"
            disabled={Boolean(formData.is_station)}
            helperText={formData.is_station ? 'У станции исполнитель — сотрудник по бейджу' : ''}
          />
          <TextField
            label="Табельный номер (QR-бейдж)"
            value={formData.employee_number}
            onChange={(e) => setFormData({ ...formData, employee_number: e.target.value })}
            fullWidth
            size="small"
            disabled={Boolean(formData.is_station)}
            helperText="Строка в QR. Пусто = номер пользователя после сохранения"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Роль</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              label="Роль"
            >
              <MenuItem value="admin">Администратор</MenuItem>
              <MenuItem value="manager">Менеджер</MenuItem>
              <MenuItem value="worker">Сотрудник</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(formData.is_station)}
                onChange={(e) => setFormData({
                  ...formData,
                  is_station: e.target.checked,
                  ...(e.target.checked ? { ms_id: '', employee_number: '', role: 'worker' } : {}),
                })}
              />
            }
            label="Станция цеха (только «Цех»; печать этикеток — отдельным правом)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Активен"
          />
          {user && (
            <Button
              startIcon={<SecurityIcon />}
              onClick={() => {
                handleClose();
                onPermissionsOpen(user);
              }}
              variant="outlined"
            >
              Настроить права доступа
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Отмена</Button>
        <Button onClick={handleSubmit} variant="contained">Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Users() {
  const { success: showSuccess, error: showError } = useNotify();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [permissionGroups, setPermissionGroups] = useState(FALLBACK_PERMISSION_GROUPS);
  const [permNames, setPermNames] = useState(() => buildFlatNames(FALLBACK_PERMISSION_GROUPS));
  const currentUser = useSelector((state) => state.auth.user);

  // Фильтры списка
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // по умолчанию скрываем неактивных
  const [permFilter, setPermFilter] = useState('all');

  // Проверка права текущего пользователя (admin имеет всё)
  const hasPerm = (key) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    const perms = currentUser.permissions || {};
    return perms[key] === true;
  };
  const canCreate = hasPerm('usersCreate');
  const canEditPermissions = hasPerm('usersPermissions');

  // Загрузка структуры групп прав с API
  const fetchPermissionGroups = useCallback(async () => {
    try {
      const res = await axios.get('/users/permission-groups');
      if (res.data?.groups) {
        setPermissionGroups(res.data.groups);
        setPermNames(buildFlatNames(res.data.groups));
      }
    } catch {
      // используем фоллбэк
    }
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users');
      const parsedUsers = response.data.map((u) => {
        let perms = u.permissions;
        if (typeof perms === 'string') {
          try {
            perms = JSON.parse(perms);
            if (typeof perms === 'string') perms = JSON.parse(perms);
          } catch (e) {
            perms = {};
          }
        }
        return { ...u, permissions: perms };
      });
      setUsers(parsedUsers);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке пользователей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPermissionGroups();
  }, [fetchPermissionGroups]);

  const handleOpenDialog = (user = null) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSaveUser = async (userData) => {
    try {
      if (selectedUser) {
        await axios.put(`/users/${selectedUser.id}`, userData);
        showSuccess('Пользователь успешно обновлен');
      } else {
        await axios.post('/users', userData);
        showSuccess('Пользователь успешно создан');
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      showError(err.response?.data?.message || 'Произошла ошибка');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await axios.put(`/users/${userId}`, { isActive: !currentStatus });
      showSuccess(`Пользователь ${currentStatus ? 'деактивирован' : 'активирован'}`);
      fetchUsers();
    } catch (err) {
      showError(err.response?.data?.message || 'Произошла ошибка');
    }
  };

  const handlePermissionsSave = async (userId, newPermissions, dataScope) => {
    try {
      await axios.put(`/users/${userId}/permissions`, { permissions: newPermissions, data_scope: dataScope });
      showSuccess('Права доступа успешно обновлены');
      fetchUsers();
    } catch (error) {
      // 400 (неизвестная org / невалидный mp) и 403 (эскалация) — показываем сообщение из ответа
      showError(error.response?.data?.message || 'Ошибка при обновлении прав доступа');
      throw error; // не закрывать диалог при ошибке
    }
  };

  const handlePermissionsOpen = (user) => {
    setSelectedUser(user);
    setPermissionsDialogOpen(true);
  };

  const getRoleChipStyle = (role) => {
    const styles = {
      admin: { bgcolor: '#ffcdd2', color: '#c62828' },
      manager: { bgcolor: '#ffe0b2', color: '#e65100' },
      worker: { bgcolor: '#bbdefb', color: '#1565c0' },
    };
    return styles[role] || { bgcolor: '#eeeeee', color: '#616161' };
  };

  const getRoleName = (role) => {
    const names = { admin: 'Администратор', manager: 'Менеджер', worker: 'Сотрудник' };
    return names[role] || role;
  };

  const cellStyle = { py: 1, px: 1, fontSize: '0.85rem' };
  const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };

  // Клиентская фильтрация списка
  const filteredUsers = users.filter((u) => {
    // Поиск по имени/фамилии/email
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = `${u.firstName || ''} ${u.lastName || ''} ${u.email || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    // Роль
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    // Статус
    if (statusFilter === 'active' && !u.isActive) return false;
    if (statusFilter === 'inactive' && u.isActive) return false;
    // Право доступа
    if (permFilter !== 'all') {
      if (u.role === 'admin') {
        // admin имеет все права всегда
      } else if (!u.permissions?.[permFilter]) {
        return false;
      }
    }
    return true;
  });

  const activeFiltersCount =
    (search.trim() ? 1 : 0) +
    (roleFilter !== 'all' ? 1 : 0) +
    (statusFilter !== 'active' ? 1 : 0) +
    (permFilter !== 'all' ? 1 : 0);

  const resetFilters = () => {
    setSearch('');
    setRoleFilter('all');
    setStatusFilter('active');
    setPermFilter('all');
  };

  // Плоский список прав для селекта фильтра (сгруппированный по группам)
  const permFilterOptions = permissionGroups.flatMap((grp) =>
    Object.entries(grp.permissions).map(([key, label]) => ({ key, label, group: grp.group }))
  );

  const colSpan = (canCreate || canEditPermissions) ? 7 : 6;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" fontWeight="bold">Сотрудники</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Добавить
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Панель фильтров */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Поиск по имени, фамилии, email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', md: 280 }, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Роль</InputLabel>
            <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} label="Роль">
              <MenuItem value="all">Все роли</MenuItem>
              <MenuItem value="admin">Администратор</MenuItem>
              <MenuItem value="manager">Менеджер</MenuItem>
              <MenuItem value="worker">Сотрудник</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Статус</InputLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Статус">
              <MenuItem value="active">Только активные</MenuItem>
              <MenuItem value="inactive">Только неактивные</MenuItem>
              <MenuItem value="all">Все</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Право доступа</InputLabel>
            <Select
              value={permFilter}
              onChange={(e) => setPermFilter(e.target.value)}
              label="Право доступа"
              MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
            >
              <MenuItem value="all">Любые права</MenuItem>
              {permFilterOptions.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {activeFiltersCount > 0 && (
            <Button size="small" onClick={resetFilters} startIcon={<ClearIcon />} sx={{ whiteSpace: 'nowrap' }}>
              Сбросить
            </Button>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ml: { md: 'auto' }, whiteSpace: 'nowrap' }}>
            Найдено: {filteredUsers.length} из {users.length}
          </Typography>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerStyle}>Имя</TableCell>
              <TableCell sx={headerStyle}>№</TableCell>
              <TableCell sx={headerStyle}>Email</TableCell>
              <TableCell sx={headerStyle}>Роль</TableCell>
              <TableCell sx={headerStyle}>Статус</TableCell>
              <TableCell sx={headerStyle}>Права доступа</TableCell>
              {(canCreate || canEditPermissions) && <TableCell sx={headerStyle} align="right">Действия</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>Нет пользователей</TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  Никто не найден по заданным фильтрам
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} sx={{ opacity: user.isActive ? 1 : 0.6 }}>
                  <TableCell sx={cellStyle}>{user.firstName} {user.lastName}</TableCell>
                  <TableCell sx={cellStyle}>{user.employee_number || user.id}</TableCell>
                  <TableCell sx={cellStyle}>{user.email}</TableCell>
                  <TableCell sx={cellStyle}>
                    {user.is_station ? (
                      <Chip label="Станция" size="small" sx={{ bgcolor: '#e0e0e0', color: '#424242' }} />
                    ) : null}
                    <Chip label={getRoleName(user.role)} size="small" sx={getRoleChipStyle(user.role)} />
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    <Chip
                      icon={user.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                      label={user.isActive ? 'Активен' : 'Неактивен'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {permissionGroups.map((grp) => {
                        const active = Object.keys(grp.permissions).filter(k => user.permissions?.[k]);
                        if (active.length === 0) return null;
                        return active.map(k => (
                          <Chip key={k} label={permNames[k] || k} size="small" sx={{ height: 20 }} />
                        ));
                      })}
                    </Box>
                  </TableCell>
                  {(canCreate || canEditPermissions) && (
                    <TableCell sx={cellStyle} align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {canCreate && (
                          <Tooltip title="Редактировать">
                            <IconButton size="small" onClick={() => handleOpenDialog(user)} disabled={user.id === currentUser?.id}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEditPermissions && (
                          <Tooltip title="Настроить права">
                            <IconButton size="small" onClick={() => handlePermissionsOpen(user)} disabled={user.id === currentUser?.id}>
                              <SecurityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canCreate && (
                          <Tooltip title={user.isActive ? 'Деактивировать' : 'Активировать'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(user.id, user.isActive)}
                              disabled={user.id === currentUser?.id}
                              color={user.isActive ? 'error' : 'success'}
                            >
                              {user.isActive ? <DeleteIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UserDialog
        open={dialogOpen}
        handleClose={handleCloseDialog}
        user={selectedUser}
        onSave={handleSaveUser}
        onPermissionsOpen={handlePermissionsOpen}
      />

      <PermissionsDialog
        open={permissionsDialogOpen}
        onClose={() => setPermissionsDialogOpen(false)}
        user={selectedUser}
        onSave={(newPermissions, dataScope) => handlePermissionsSave(selectedUser?.id, newPermissions, dataScope)}
        permissionGroups={permissionGroups}
      />
    </Box>
  );
}
